import { Link } from 'react-router-dom'
import { ArrowLeft, MessageCircle, Info, BotMessageSquare, Languages, Volume2, Users } from 'lucide-react'
import { TextToSpeechGallery } from '@/components/gallery/TextToSpeechGallery'
import { SpeechGalleryItem } from '@/models/gallery'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/SpeechPage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Gemini Speech',
    shortName: 'Gemini',
    modelKey: 'gemini/gemini-2.5-pro-preview-tts',
    creditsPerRequest: 0.3,
    voiceCount: 30,
    languageCount: 24,
    heroAudioSrc: 'https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-hero.wav',
    providerUrl: 'https://ai.google.dev/gemini-api/docs/speech-generation',
    providerName: 'Google Gemini',
    pageUrl: 'https://docs.aibrush.co/speech-models/gemini-speech',
} as const;

const textToSpeechGalleryItems: SpeechGalleryItem[] = [
    {
        id: "gemini-speech-1",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-1.wav",
        className: "speech-item",
        prompt: "What a ripper of a shot! That's gone for six, fair dinkum! The crowd here is absolutely going wild, you beauty! This is proper cricket, folks, edge-of-your-seat stuff! The Aussies are absolutely tearing into them, putting on a clinic out here. You can feel the energy in the stadium, it's electric! What a game, what a performance!",
        tone: "Highly energetic and animated, with a distinct Australian accent, embodying a passionate and slightly boisterous cricket commentator. The delivery should be fast-paced, full of excitement, and include exclamations that convey the thrill of the game.",
        voice: "Achernar",
        voiceDescription: "(Female) Soft"
    },
    {
        id: "gemini-speech-2",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-2.wav",
        className: "speech-item",
        prompt: "Listen closely, mes amis, as the embers glow. Deep in the forgotten woods, where the mist clings to ancient oaks, stands a house… always silent. They say a lonely painter lived there, his brushes dancing with shadows. One night, a traveler, lost in the fog, found its door ajar. A faint melody, like a broken music box, drifted from within. He stepped inside, and the air grew cold, heavy with unspoken secrets....",
        tone: "Mysterious and eerie, with a hushed, whispering quality, delivered with a subtle French accent, like a storyteller around a campfire. The pace should be slow and deliberate, building suspense, with a slight emphasis on chilling details like 'whispers,' 'no face,' and 'cold breath'.",
        voice: "Achird",
        voiceDescription: "(Male) Friendly"
    },
    {
        id: "gemini-speech-3",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-3.wav",
        className: "speech-item",
        prompt: "The twilight deepens, and with it, the ache within my breast. A ghost of laughter echoes in the quiet air, a melody once shared, now a mournful, solitary strain. Oh, the cruel beauty of memory, that grants us glimpses of what was, only to underscore the hollow where love once bloomed. Each fading light, a reminder of the brilliance we extinguished. And I, a solitary sentinel, guarding the ruins of a heart that once knew spring.",
        tone: "Melancholic and somber, with a profound sense of loss and longing, delivered with a gentle British accent, a gravelly voice, and a slow, reflective pace. Emphasize 'ache', 'ghost of laughter', 'hollow', 'ruins'.",
        voice: "Algenib",
        voiceDescription: "(Male) Gravelly"
    },
    {
        id: "gemini-speech-4",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-4.wav",
        className: "speech-item",
        prompt: "Ah, my little bambinos, come closer. Let Nonno tell you a story, not from a book, but from a time long ago, when the world was... different. The skies weren't always so blue, and the laughter was sometimes quiet, like a secret. We saw many things, things that made us grow up quickly. But even in those grey days, there were small moments of sunshine – a shared piece of bread, a song whispered in the dark, the warmth of family holding tight",
        tone: "Warm, reflective, and gentle, with a slight Italian accent, reminiscent of a wise, elderly grandfather. The delivery should be slow and measured, conveying a sense of nostalgia, resilience, and a deep love for family. Emphasize 'small moments of sunshine' and 'spring always comes.'",
        voice: "Algieba",
        voiceDescription: "(Male) Smooth"
    },
    {
        id: "gemini-speech-5",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-5.wav",
        className: "speech-item",
        prompt: "How much more, I ask you, how much more will we endure?! For too long, the chains of injustice have held our people down! They build their empires on our backs, on our broken promises, on the very sweat and tears of honest folk! And for what? For their profit! For their power! Well, I say no more! No more silent suffering! No more bowed heads! We will rise! We will roar! And we will reclaim what is ours! This land is our land, and its justice shall be our justice! FREEDOM!",
        tone: "Angry, passionate, and defiant Scottish activist, with a gravelly voice, delivering a powerful and rallying speech. Emphasize 'chains of injustice', 'no more', 'rise', 'roar', and 'FREEDOM!'.",
        voice: "Alnilam",
        voiceDescription: "(Male) Firm"
    },
    {
        id: "gemini-speech-6",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-6.wav",
        className: "speech-item",
        prompt: "Minha querida, em cada estrela vejo o brilho dos teus olhos, e em cada canção, a melodia do meu coração que te pertence. A tua presença é o sol que ilumina a minha alma, a brisa suave que acalma os meus pensamentos. Desde o momento em que te vi, soube que o meu destino estava entrelaçado ao teu. Tu és o meu fado, a minha paixão, o amor da minha vida. Contigo, cada dia é um poema, e cada beijo, uma eternidade. Eu te amo, hoje, amanhã, e para sempre.",
        tone: "Romantic, passionate, and full of tender emotion, with a slight Portuguese accent, delivered with a smooth, resonant voice, as if singing a heartfelt ballad. Emphasize 'alma', 'coração', and 'para sempre'.",
        voice: "Aoede",
        voiceDescription: "(Female) Breezy",
    },
    {
        id: "gemini-speech-7",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-7.wav",
        className: "speech-item",
        prompt: "Like, how can a computer 'learn' without actually experiencing things? Is it just super advanced pattern matching, or is there something more? And how does it even know what's 'right' or 'wrong' without feelings? It's just algorithms, but it acts almost human sometimes. Is that just programmed, or is there a tiny bit of actual 'thinking' happening? My brain totally short-circuits trying to figure it out!",
        tone: "Curious, puzzled, and earnest, delivered by a youthful Korean teenager, with a slight upward inflection on questions and a sense of genuine confusion but eagerness to understand.",
        voice: "Autonoe",
        voiceDescription: "(Female) Bright"
    },
    {
        id: "gemini-speech-8",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-8.wav",
        className: "speech-item",
        prompt: "I've had enough! Look at this mess! Does anyone ever listen to me?! I slave away for all of you, and what do I get? Nothing! Just more demands, more chaos! I'm so tired of this! [crying] Can't you see I'm breaking?! All I ask for is a little help, a little respect! But no, you just take, and take, and take! [shouts] I can't do this anymore! I can't!",
        tone: "Furious, exhausted, and heartbroken, bordering on hysterical, with a voice that cracks from emotion, escalating from frustrated complaints to desperate shouting and sobbing. Convey deep resentment and a feeling of being completely overwhelmed and unappreciated.",
        voice: "Callirrhoe",
        voiceDescription: "(Female) Easy-going"
    },
    {
        id: "gemini-speech-9",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-9.wav",
        className: "speech-item",
        prompt: "Guten Abend. Hier sind die wichtigsten Meldungen des Tages. In der Politik stehen die Verhandlungen über das neue Klimaschutzabkommen kurz vor dem Abschluss. Beobachter erwarten einen Durchbruch in den kommenden Stunden. Wirtschaftlich gesehen, hat der globale Aktienmarkt heute leichte Gewinne verzeichnet, angetrieben durch positive Daten aus dem Technologiesektor.",
        tone: "Neutral, clear, and authoritative, suitable for a German news anchor reporting on global events. The delivery should be measured and informative, with precise pronunciation and a calm, steady pace.",
        voice: "Charon",
        voiceDescription: "(Male) Informative"
    },
    {
        id: "gemini-speech-10",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-10.wav",
        className: "speech-item",
        prompt: "Mabuhay! Eto na ang party! Come on in, my beautiful people! The adobo's hot, the music's pumping, and tonight, we celebrate life, laughter, and a little bit of kalokohan! Let's make some memories, shall we? Tara na! Let's get this party started! Woohoo!",
        tone: "Extremely vivacious, warm, and joyful, with a strong Filipino accent and an infectious, high-energy delivery, encouraging guests to join the celebration.",
        voice: "Despina",
        voiceDescription: "(Female) Smooth"
    },
    {
        id: "gemini-speech-11",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-11.wav",
        className: "speech-item",
        prompt: "Greetings, organic life forms. This unit will now provide an elementary overview of quantum physics. Observe: In the classical realm, particles possess precise positions and momenta. However, at the subatomic scale, this certainty dissolves. Particles exist in superposition, simultaneously occupying multiple states until measured. This phenomenon is known as the wave-particle duality",
        tone: "Monotone, purely factual, and precise, as delivered by an AI assistant designed for educational purposes. The delivery should be devoid of emotion, with clear enunciation and a steady, unchanging rhythm.",
        voice: "Enceladus",
        voiceDescription: "(Male) Breathy"
    },
    {
        id: "gemini-speech-12",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-12.wav",
        className: "speech-item",
        prompt: "Atenção a todos os civis e pessoal de serviço! Repito: atenção! Esta é uma ordem de evacuação imediata. A situação é crítica e a segurança de todos é a nossa prioridade máxima. Sigam as rotas de emergência designadas! Não há tempo para hesitar! Ajude quem precisar, mas mantenham o ritmo! Movam-se com urgência, mas sem pânico! Encontrem o ponto de encontro mais próximo. As equipes de resgate estão a caminho. Mantenham a calma e obedeçam às instruções! Pela segurança de todos, AVANCEM!",
        tone: "Authoritative, urgent, and commanding, with a strong Brazilian accent. The delivery should be direct, firm, and convey a sense of immediate danger and decisive action, emphasizing 'imediata', 'crítica', 'prioridade máxima', and 'AVANCEM!",
        voice: "Erinome",
        voiceDescription: "(Female) Clear"
    },
    {
        id: "gemini-speech-13",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-13.wav",
        className: "speech-item",
        prompt: "In Russia, we have saying: 'Why buy new socks when old ones still have holes? Is for ventilation!' My babushka, she says, 'Is good for air circulation, keeps feet fresh!' And then she hits me with rolling pin for wearing too many holes. Life is good, da?",
        tone: "Humorous, laughing, Russian comedian, with a hearty laugh and playful self-deprecation.",
        voice: "Fenrir",
        voiceDescription: "(Male) Excitable"
    },
    {
        id: "gemini-speech-14",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-14.wav",
        className: "speech-item",
        prompt: "The wind howls like a banshee, and the sea, oh, the sea is rising with a terrible rage! We've little time, neighbours, little time at all! Get to the high ground, now, before the waves consume us whole! May God have mercy on our souls!",
        tone: "A trembling, fearful Irish villager, breathless with terror, urgently warning others of an approaching storm. The voice should convey desperation and a strong sense of impending doom.",
        voice: "Gacrux",
        voiceDescription: "(Female) Mature"
    },
    {
        id: "gemini-speech-15",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-15.wav",
        className: "speech-item",
        prompt: `My old town, sleeping among the mountains, Every street, every corner, a memory woven. The aroma of coffee, in mornings of yesteryear, And the echo of laughter, that causes me no harm.`,
        tone: "Nostalgic, warm, and heartfelt, like an old folk singer strumming a guitar, with a gentle, melancholic rhythm and a deep sense of affection for home.",
        voice: "Iapetus",
        voiceDescription: "(Male) Clear"
    },
    {
        id: "gemini-speech-16",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-16.wav",
        className: "speech-item",
        prompt: "The breath, a fleeting whisper. The thought, a momentary cloud. This body, a vessel of dust and starlight, ever-changing, ever-becoming. Is existence then but a dance of impermanence, a symphony of echoes in the vast, silent hall of eternity? Or does within each fleeting moment, a timeless truth reside, patiently awaiting the quiet heart to perceive it? The river flows, the mountain stands, yet both are ceaselessly transformed. So too, is the self. Not a fixed point, but a journey",
        tone: "Serene, contemplative, wise, and peaceful, delivered with the gentle cadence of a Tibetan monk, encouraging deep thought and inner peace. Emphasize 'impermanence, ' 'timeless truth, ' and 'profound peace.'",
        voice: "Kore",
        voiceDescription: "(Female) Firm",
    },
    {
        id: "gemini-speech-17",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-17.wav",
        className: "speech-item",
        prompt: "Dear family and friends, today we bid farewell to a beloved person whose light will shine forever in our hearts. It is difficult to find words for the sorrow we feel, this emptiness that remains. [sighs] But amidst our tears, we cherish the warmth of the memories, the love that was so rich, and the moments that will stay with us forever. We will miss you, more than words can say.",
        tone: "Sorrowful, solemn, and deeply compassionate, delivered with a gentle, comforting cadence, appropriate for a eulogy at a Dutch funeral. The voice should convey empathy and respect, with deliberate pacing to allow for reflection.",
        voice: "Laomedeia",
        voiceDescription: "(Female) Upbeat"
    },
    {
        id: "gemini-speech-18",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-18.wav",
        className: "speech-item",
        prompt: `Little bears, little bears, time to wake up, desu! Tiny trains, tiny trains, choo-choo, zoom-zoom, desu! Dolly-chan, dolly-chan, let's all play now, desu! Tee-hee-hee, tee-hee-hee, happy time for you, desu`,
        tone: "A giggling child's voice, very playful and innocent, with a light Japanese accent, singing a nursery rhyme to toys. Emphasize the giggles and the playful nature.",
        voice: "Leda",
        voiceDescription: "(Female) Youthful"
    },
    {
        id: "gemini-speech-19",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-19.wav",
        className: "speech-item",
        prompt: "Class, attention! Today, we embark on a journey not merely through dates and names, but into the very essence of human endeavor. History is not a collection of isolated events; it is a continuous, interconnected tapestry woven by the choices, conflicts, and compromises of those who came before us. To disregard history is to navigate the future blindly, condemned to repeat the errors of the past",
        tone: "A stern, authoritative, and highly intellectual Chinese professor, delivering a formal lecture with gravitas and a measured pace. The tone should convey deep knowledge, a demand for attention, and a subtle sense of wisdom. Emphasis on clarity, precision, and the weight of historical understanding.",
        voice: "Orus",
        voiceDescription: "(Male) Firm"
    },
    {
        id: "gemini-speech-20",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-20.wav",
        className: "speech-item",
        prompt: "Oh, modern art, bless its cotton socks. Where else can you find a banana taped to a wall selling for six figures, only to be eaten by a performance artist who then claims *that's* the real masterpiece? It's less about talent and more about 'tude, isn't it? As if the emperor's new clothes were painted by a toddler and hailed as visionary. And the deeper you try to 'understand' it, the more you realize it's just a grand, expensive joke, usually on the buyer",
        tone: "witty and sarcastic, delivered with a slight Canadian accent, dry and condescending but with a charming underlying amusement, emphasizing key phrases with a raised eyebrow implied.",
        voice: "Puck",
        voiceDescription: "(Male) Upbeat"
    },
    {
        id: "gemini-speech-21",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-21.wav",
        className: "speech-item",
        prompt: "¡Queridos amigos y familia! ¡Qué alegria inmensa nos embarga esta noche! Con el corazón rebosante de amor y felicidad, tengo el honor de anunciarles la unión de [Nombre del Novio] y [Nombre de la Novia] en sagrado matrimonio. ¡Que viva el amor! ¡Que viva la pareja! Brindemos por su felicidad eterna, por cada risa compartida, por cada sueño que construirán juntos. ¡Felicidades a los recién casados!",
        tone: "Joyful, enthusiastic, and celebratory, like an upbeat Spanish celebrant at a wedding party. The delivery should be energetic and include exclamations of happiness and warm wishes, with a clear and charming Spanish accent.",
        voice: "Pulcherrima",
        voiceDescription: "(Female) Forward"
    },
    {
        id: "gemini-speech-22",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-22.wav",
        className: "speech-item",
        prompt: "Alright, team! Gather 'round, listen up. Look, I see the fire in your eyes, the grit in your teeth. But talent alone, my friends, is not enough. Success? Ah, that's built brick by brick, sweat by sweat. It's in those early mornings when no one's watching, in those extra reps when your body screams 'stop.' It's about showing up, even when you're tired, even when you doubt. Because champions aren't born; they're forged",
        tone: "Empowering, motivational, strong, inspiring, with a clear and passionate South African accent, full of conviction and drive, direct and encouraging, like a seasoned coach igniting their team before a big match. Emphasize 'fire', 'grit', 'champions', 'dominate', 'inspire'.",
        voice: "Rasalgethi",
        voiceDescription: "(Male) Informative"
    },
    {
        id: "gemini-speech-23",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-23.wav",
        className: "speech-item",
        prompt: `You guys aren't going to believe what happened up there! So, we're deep in the Rockies, right? The air was thin, the peaks were just *stunning*, and then, out of nowhere, this blizzard hits! I mean, a real whiteout. We thought we were done for, but then, through the snow, we saw this tiny, flickering light... and it led us straight to an old, forgotten trapper's cabin! Inside, a roaring fire, some old maps, and a journal detailing routes no one's seen in a century!`,
        tone: "Excited, enthusiastic American explorer, with a sense of wonder and thrill, recounting a breathtaking adventure to close friends, emphasizing the danger and the incredible discovery.",
        voice: "Sadachbia",
        voiceDescription: "(Male) Lively"
    },
    {
        id: "gemini-speech-24",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-24.wav",
        className: "speech-item",
        prompt: "Welcome, dear ones, to this moment of stillness. Settle gently into your space, allowing your body to soften, your mind to quiet. Close your eyes, and bring your awareness to your breath. Feel the gentle rise and fall of your abdomen, the cool air entering, the warm air leaving. With each inhale, draw in peace. With each exhale, release all tension, all worries. Let your breath be your anchor, grounding you in the present....",
        tone: "Serene, calm, and deeply peaceful, with a gentle, flowing rhythm and a wise, reassuring quality, as if delivered by a meditative Indian guru. The voice should be smooth and resonant, encouraging deep relaxation and inner peace.",
        voice: "Sadaltager",
        voiceDescription: "(Male) Knowledgeable"
    },
    {
        id: "gemini-speech-25",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-25.wav",
        className: "speech-item",
        prompt: "We fought, we bled, we conquered! By the mighty Zeus, victory is ours! Today, the gods themselves smiled upon us, and our spears tasted the glory of triumph! Let the echoes of our valor resound across the lands, a testament to the might of Sparta! For freedom! For glory! For Hellas!",
        tone: "Triumphant, powerful, and commanding, delivered with the booming voice of a proud Greek warrior, emphasizing valor and victory. The tone should convey immense pride and a sense of divine favor.",
        voice: "Schedar",
        voiceDescription: "(Male) Even"
    },
    {
        id: "gemini-speech-26",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-26.wav",
        className: "speech-item",
        prompt: "Dzień dobry, my dear friends, and welcome to my kitchen! Today, we're not just cooking; we're embarking on a culinary adventure, filled with flavors, laughter, and a little bit of magic, eh? First, always start with fresh ingredients—they are the soul of our dishes. Remember what Babcia always said: 'A good meal begins at the market, not just at the stove!' Now, don't be afraid to experiment, to taste as you go.",
        tone: "Cheerful, warm, and inviting, with a distinct Polish accent and a friendly, slightly humorous delivery, like a beloved chef sharing family secrets.",
        voice: "Sulafat",
        voiceDescription: "(Female) Warm"
    },
    {
        id: "gemini-speech-27",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-27.wav",
        className: "speech-item",
        prompt: "Alright, team, gather 'round! The stars ain't just pinpricks in the night sky, they're an open invitation, a cosmic frontier just waitin' for us to plant our flag! We've got the brightest minds, the steadiest hands, and a spirit as vast as the universe itself. So let's dust off those dreams, fuel up those rockets, and show the cosmos what a little Texan grit and good ol' American ingenuity can do!",
        tone: "Optimistic, adventurous, and motivating, delivered with a strong, friendly Texan drawl, inspiring confidence and a sense of shared purpose.",
        voice: "Umbriel",
        voiceDescription: "(Male) Easy-going"
    },
    {
        id: "gemini-speech-28",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-28.wav",
        className: "speech-item",
        prompt: "In the heart of the whispering pines, where the northern lights dance their sleepy waltz, lives a little Swedish elf named Lumina. Her hair, spun from moonbeams, glows softly, and her laughter tinkles like tiny bells. Every night, when the stars begin to peek, Lumina visits the dreams of kind children. She sprinkles stardust on their pillows, whispers secrets of enchanted forests, and paints dreams of friendly bears and singing flowers",
        tone: "Whispering, gentle, magical, and warm, like a Swedish elf telling a bedtime story, with a hint of a Scandinavian lilt. The delivery should be slow and soothing, perfect for lulling children to sleep.",
        voice: "Vindemiatrix",
        voiceDescription: "(Female) Gentle"
    },
    {
        id: "gemini-speech-29",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-29.wav",
        className: "speech-item",
        prompt: "Sleep, sleep, little child, soon to a new morning. Sleep rises, eyes closed, like a flower bud. A small bird sleeps in the tree, the forest sighs quietly. Mother's embrace is warm, you are always safe. Sleep, sleep, my darling, dreams carry you. Tomorrow is a new day, full of joy and sunshine.",
        tone: "Soothing and gentle, like a mother humming a lullaby to her baby, with a calm and tender delivery and a Finnish accent.",
        voice: "Zephyr",
        voiceDescription: "(Female) Bright",
    },
    {
        id: "gemini-speech-30",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-30.wav",
        className: "speech-item",
        prompt: "Hey rivals! This is my arena! Come if you dare! Let's see whose blood is hotter! Who do you think you are to stand against me?! Come on, roar! I am the lion of this land! Tremble before the Turkish might standing before you! This war is ours! My last words: YA ALLAH, YA BISMILLAH!.",
        tone: "Furious, aggressive, and highly intimidating, delivered with the raw power of a bold Turkish fighter, emphasizing defiance and a challenge to battle. Each phrase should be a roar, conveying absolute conviction and a readiness for combat.",
        voice: "Zubenelgenubi",
        voiceDescription: "(Male) Casual"
    }
];

const textToSpeechGalleryItemsMulti: SpeechGalleryItem[] = [
    {
        id: "gemini-speech-31",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-31.wav",
        className: "speech-item",
        prompt: `Speaker 1: The latest data clearly points to a green industrial revolution, offering unprecedented opportunities and mitigating climate change! Speaker 2: Opportunities? Or just more taxes and regulations hindering our growth? This 'revolution' sounds more like a financial burden than a solution, based on what I'm seeing.`,
        tone: `Speaker 1: Optimistic, enthusiastic, and confident. Speaker 2: Skeptical, dry, and slightly condescending.`,
        voice: "Umbriel",
        voiceDescription: "(Male) Easy-going",
        voice2: "Algenib",
        voice2Description: "(Male) Gravelly",
    },
    {
        id: "gemini-speech-32",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-32.wav",
        className: "speech-item",
        prompt: `Speaker 1: G'day, mate! Always keen to hear what really stirs someone's soul on the silver screen. What's a film that truly speaks to ya, eh? Speaker 2: Ah, that is a profound question. For me, perhaps 'Spirited Away.' Its layers of meaning, the quiet beauty, the poignant journey... it resonates deeply. What about you, my friend?`,
        tone: `Speaker 1: Cheerful, enthusiastic. Speaker 2: Thoughtful, serene.`,
        voice: "Achird",
        voiceDescription: "(Male) Friendly",
        voice2: "Despina",
        voice2Description: "(Female) Smooth"
    },
    {
        id: "gemini-speech-33",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-33.wav",
        className: "speech-item",
        prompt: `Speaker 1: Mr. Schmidt, this proposal is a golden opportunity. Our projections are robust, and the market analysis speaks for itself. Speaker 2: Frau Sharma, I appreciate your enthusiasm, but I must confess, the projected returns seem... ambitious. My team's risk assessment highlights several potential pitfalls.`,
        tone: `Speaker 1: Confident, persuasive. Speaker 2: Hesitant, analytical, and cautious.`,
        voice: "Despina",
        voiceDescription: "(Female) Smooth",
        voice2: "Charon",
        voice2Description: "(Male) Informative",
    },
    {
        id: "gemini-speech-34",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-34.wav",
        className: "speech-item",
        prompt: `Speaker 1: Oh là là, chérie! You won't *believe* what I heard about Monsieur Dubois! It's simply scandalous! Speaker 2: Non! Tell me tout de suite! Is it about his new film, or... *that* actress? My heart is beating so fast!`,
        tone: `A lively gossip session, very excited, with French accents, rapid-fire, animated, and dramatic pauses for effect.`,
        voice: "Autonoe",
        voiceDescription: "(Female) Bright",
        voice2: "Laomedeia",
        voice2Description: "(Female) Upbeat",
    },
    {
        id: "gemini-speech-35",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-35.wav",
        className: "speech-item",
        prompt: `Speaker 1: Life, young one, is like a river. It flows, changes, yet its essence remains. What do you seek in its currents? Speaker 2: Maestro, I seek meaning! A purpose beyond the daily grind. Is it found in love, art, or the simple joy of pasta?`,
        tone: `A serene, philosophical discussion. Speaker 1: Wise, reflective. Speaker 2: Inquisitive, eager.`,
        voice: "Iapetus",
        voiceDescription: "(Male) Clear",
        voice2: "Algieba",
        voice2Description: "(Male) Smooth",
    },
    {
        id: "gemini-speech-36",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-36.wav",
        className: "speech-item",
        prompt: `Speaker 1: Hola, señorita. Is this seat taken? Your smile, it lights up this whole cafe. Speaker 2: Oh, uhm, no. Please. Thank you. Speaker 1: Magnífico. I am Ricardo. And you, my shy blossom? Speaker 2: Elsa. It's... nice to meet you.`,
        tone: `Speaker 1: Charming, flirtatious. Speaker 2: Shy, slightly nervous.`,
        voice: "Algieba",
        voiceDescription: "(Male) Smooth",
        voice2: "Vindemiatrix",
        voice2Description: "(Female) Gentle",
    },
    {
        id: "gemini-speech-37",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-37.wav",
        className: "speech-item",
        prompt: `Speaker 1: Mas como é que você pode dizer uma coisa dessas?! O futebol brasileiro é arte, paixão! Os nossos jogadores têm gingado, alma! Speaker 2: Minha querida, paixão é uma coisa, mas tática e inteligência são outras. Nós jogamos com a cabeça, não só com o coração.`,
        tone: `Speaker 1: Passionate, fiery. Speaker 2: Amused, calm, and slightly condescending.`,
        voice: "Pulcherrima",
        voiceDescription: "(Female) Forward",
        voice2: "Algieba",
        voice2Description: "(Male) Smooth",
    },
    {
        id: "gemini-speech-38",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-38.wav",
        className: "speech-item",
        prompt: `Speaker 1: Alright, eh? So for this trip, I'm thinking Rockies or maybe East Coast trails? Speaker 2: Ooh, tough choice! But you know me, I'm always up for a good mountain challenge. What about Banff, then? Speaker 1: Classic! And afterwards, poutine? Speaker 2: You're speaking my language!`,
        tone: `excited, collaborative, and friendly, with a hint of Canadian enthusiasm`,
        voice: "Zephyr",
        voiceDescription: "(Female) Bright",
        voice2: "Aoede",
        voice2Description: "(Female) Breezy",
    },
    {
        id: "gemini-speech-39",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-39.wav",
        className: "speech-item",
        prompt: `Speaker 1: Alright, my friend. Let us examine this 'gadget' of yours. Is it properly connected? Speaker 2: Ugh, yes! Of course it's plugged in! I've tried everything! Speaker 1: Simple, you say? Perhaps too simple.`,
        tone: `Speaker 1: Knowledgeable, calm. Speaker 2: Frustrated, impatient.`,
        voice: "Fenrir",
        voiceDescription: "(Male) Excitable",
        voice2: "Autonoe",
        voice2Description: "(Female) Bright",
    },
    {
        id: "gemini-speech-40",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-40.wav",
        className: "speech-item",
        prompt: `Speaker 1: Another new dress? Do you have any idea how much we spent this month? Speaker 2: It's just one dress! And what about that new gadget you bought? Speaker 1: That's for work! You always twist things around!`,
        tone: `Heated, frustrated marital dispute about finances.`,
        voice: "Charon",
        voiceDescription: "(Male) Informative",
        voice2: "Zephyr",
        voice2Description: "(Female) Bright",
    },
    {
        id: "gemini-speech-41",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-41.wav",
        className: "speech-item",
        prompt: `Speaker 1: Remember that time you tried to make soufflé? Speaker 2: Hey! It was 'rustic'! And at least it didn't explode like your pressure cooker chili! Speaker 1: A minor structural integrity issue, easily fixed! Speaker 2: You can't talk, Mr. 'Charred-Beyond-Recognition' Toast!`,
        tone: `Humorous, lighthearted, and playful.`,
        voice: "Puck",
        voiceDescription: "(Male) Upbeat",
        voice2: "Autonoe",
        voice2Description: "(Female) Bright",
    },
    {
        id: "gemini-speech-42",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-42.wav",
        className: "speech-item",
        prompt: `Speaker 1: The latest policy changes are deeply concerning. Speaker 2: I agree. The lack of transparency in the decision-making process is alarming. Speaker 1: And what about the long-term social consequences? Speaker 2: Precisely. It's about fundamental rights.`,
        tone: `Serious, concerned, and analytical political discourse.`,
        voice: "Kore",
        voiceDescription: "(Female) Firm",
        voice2: "Despina",
        voice2Description: "(Female) Smooth",
    },
    {
        id: "gemini-speech-43",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-43.wav",
        className: "speech-item",
        prompt: `Speaker 1: Haydi, dostum! Fitness is about the fire in your soul! Push harder! Speaker 2: Indeed, my brother! Every drop of sweat is a promise for a stronger tomorrow! Speaker 1: That's the spirit! No limits! Speaker 2: We are unstoppable!`,
        tone: `Motivational, energetic, and inspiring gym session.`,
        voice: "Fenrir",
        voiceDescription: "(Male) Excitable",
        voice2: "Orus",
        voice2Description: "(Male) Firm",
    },
    {
        id: "gemini-speech-44",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/gemini-pro-speech/sample-44.wav",
        className: "speech-item",
        prompt: `Speaker 1: Ah, the old photographs... each one a whisper from the past. My Oma smelling of fresh baked bread. Speaker 2: Yes, I understand. My grandfather taught me the value of community, of neighbors helping neighbors.`,
        tone: `Nostalgic and warm Dutch/English family conversation.`,
        voice: "Zephyr",
        voiceDescription: "(Female) Bright",
        voice2: "Charon",
        voice2Description: "(Male) Informative",
    }
];

export default function GeminiProSpeechPage() {
    return (
        <div {...stylex.props(styles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection, styles.speechHeroAnimated)}>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Experience premium <strong>Text to Speech</strong> generation with Google's most advanced speech synthesis model.
                        Featuring <strong>{MODEL_CONFIG.voiceCount} distinct voices</strong> and support for <strong>{MODEL_CONFIG.languageCount} languages</strong>.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <MessageCircle size={20} {...stylex.props(styles.statSvg)} />
                            <span>Text to Speech</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Volume2 size={20} {...stylex.props(styles.statSvg)} />
                            <span>{MODEL_CONFIG.voiceCount} Voice Options</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Languages size={20} {...stylex.props(styles.statSvg)} />
                            <span>{MODEL_CONFIG.languageCount} Languages</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Users size={20} {...stylex.props(styles.statSvg)} />
                            <span>Multi-Voice Conversations Support</span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerRequest} credits per request</div>

                    {/* Featured Voice Player */}
                    <div {...stylex.props(styles.heroVoicePlayer)}>
                        <audio controls {...stylex.props(styles.heroAudioPlayer)}>
                            <source src={MODEL_CONFIG.heroAudioSrc} type="audio/wav" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                </div>

                {/* Floating Icons Container */}
                <div {...stylex.props(styles.floatingIconsContainer)}>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon1)}>
                        <MessageCircle size={120} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon2)}>
                        <Languages size={80} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon3)}>
                        <Users size={90} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon4)}>
                        <Volume2 size={50} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                </div>
            </section>

            {/* Important Note */}
            <div {...stylex.props(styles.importantNote)}>
                <div {...stylex.props(styles.noteIcon)}>
                    <Info size={24} />
                </div>
                <div {...stylex.props(styles.noteContent)}>
                    <h4 {...stylex.props(styles.noteContentH4)}>Important Note</h4>
                    <p {...stylex.props(styles.noteContentP)}>
                        <strong>These voice examples are demonstrations of what's possible</strong> - they are not restrictive!
                        Every voice in the gallery can adapt to <strong>any language, tone, emotion, or speaking style</strong> you specify in your prompt.
                        The same voice can be cheerful, serious, excited, calm, or any other tone you need.
                        Think of these as starting points - your creativity and prompt engineering will unlock the full potential of each voice.
                    </p>
                </div>
            </div>

            {/* AI Assistance Note */}
            <div {...stylex.props(styles.importantNote)}>
                <div {...stylex.props(styles.noteIcon)}>
                    <div {...stylex.props(styles.inlineChatButton)}>
                        <BotMessageSquare size={22} />
                    </div>
                </div>
                <div {...stylex.props(styles.noteContent)}>
                    <p {...stylex.props(styles.noteContentP)}>
                        <strong>Need help with voice selection or prompt writing?</strong> Click the
                        chat icon in the top-right corner of any component to get AI assistance with your speech creation!
                    </p>
                </div>
            </div>

            {/* Speech Gallery */}
            <TextToSpeechGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.name}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerRequest} credits</strong> per second of generated audio.`}
                title="Text to Speech Gallery"
                description={`Explore the exceptional capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>Gemini 2.5 Pro/Flash TTS</a> through these AI-generated speech samples. With advanced neural networks and natural language processing, ${MODEL_CONFIG.name} delivers high-quality, human-like voice synthesis. <strong>Click on any audio sample</strong> to hear how it was created.`}
                items={textToSpeechGalleryItems} />

            {/* Multi-Voice Speech Gallery */}
            <TextToSpeechGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.name}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerRequest} credits</strong> per second of generated audio.`}
                title="Multi-Voice Conversations with Gemini"
                description={`Experience the next level of AI speech synthesis with <strong>multi-voice conversations</strong>. <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>Gemini 2.5 Pro/Flash TTS</a> enables natural dialogues between multiple speakers, perfect for creating <strong>podcasts</strong>, <strong>interviews</strong>, <strong>educational content</strong>, and <strong>interactive storytelling</strong>.`}
                items={textToSpeechGalleryItemsMulti} />
        </div>
    )
}